import React from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBrand } from '../ServerBrandProvider';
import { useGolfOSMenuItems } from '../../hooks/useGolfOSMenuItems';
import { GolfOSMark } from './GolfOSMark';

const MENU_WIDTH = 276;

const NavItem = ({
  to,
  icon,
  text,
  isActive,
}: {
  to: string;
  icon: React.ReactNode;
  text: string;
  isActive: boolean;
}) => {
  const navigate = useNavigate();
  const { brand } = useBrand();
  const primary = brand?.colors?.primary || '#2D6A2F';
  const accent = brand?.colors?.accent || '#8DC63F';

  return (
    <ListItemButton
      onClick={() => navigate(to)}
      sx={{
        borderRadius: 2,
        py: 1.25,
        px: 1.5,
        mb: 0.5,
        mx: 1,
        color: isActive ? primary : 'rgba(26, 63, 27, 0.78)',
        background: isActive
          ? `linear-gradient(90deg, rgba(45,106,47,0.14) 0%, rgba(141,198,63,0.06) 100%)`
          : 'transparent',
        borderLeft: isActive ? `3px solid ${accent}` : '3px solid transparent',
        '&:hover': {
          bgcolor: 'rgba(45, 106, 47, 0.06)',
          borderLeft: `3px solid ${primary}55`,
        },
        transition: 'background 0.2s ease, border-color 0.2s ease',
      }}
    >
      <ListItemIcon
        sx={{
          color: isActive ? primary : 'inherit',
          minWidth: 42,
        }}
      >
        {icon}
      </ListItemIcon>
      <ListItemText
        primary={text}
        primaryTypographyProps={{
          fontWeight: isActive ? 700 : 500,
          fontSize: '0.875rem',
          letterSpacing: '0.01em',
        }}
      />
    </ListItemButton>
  );
};

const SectionLabel = ({ title }: { title: string }) => {
  const { brand } = useBrand();
  const muted = brand?.colors?.secondary || '#1A3F1B';

  return (
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        px: 2.5,
        pt: 2,
        pb: 0.75,
        color: muted,
        opacity: 0.75,
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        fontSize: '0.65rem',
      }}
    >
      {title}
    </Typography>
  );
};

/**
 * GolfOS-specific sidebar: grouped labels tuned for club operations, distinct from Distill chrome.
 */
export const GolfOSMenu = () => {
  const location = useLocation();
  const { brand } = useBrand();
  const primary = brand?.colors?.primary || '#2D6A2F';
  const menuSections = useGolfOSMenuItems();

  return (
    <Box
      className="golfos-side-menu"
      sx={{
        boxShadow: '8px 0 40px rgba(26, 63, 27, 0.07)',
        background: 'linear-gradient(180deg, #ffffff 0%, #f4faf4 55%, #eef6ef 100%)',
        margin: '12px 0 12px 12px',
        borderRadius: '18px',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 1200,
        height: 'calc(100vh - 24px)',
        display: 'flex',
        flexDirection: 'column',
        width: MENU_WIDTH,
        border: `1px solid ${primary}18`,
      }}
    >
      <Box
        sx={{
          px: 2,
          pt: 2.5,
          pb: 2,
          borderBottom: `1px solid ${primary}14`,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <GolfOSMark height={40} variant="dark" />
        </Box>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            textAlign: 'center',
            mt: 1.25,
            color: primary,
            opacity: 0.85,
            fontWeight: 600,
            letterSpacing: '0.04em',
          }}
        >
          Operations hub
        </Typography>
      </Box>

      <Box sx={{ overflowY: 'auto', flex: 1, py: 1, pb: 2 }}>
        {menuSections.map((section, sectionIndex) => (
          <Box key={`${section.title}-${sectionIndex}`}>
            {sectionIndex > 0 && <Divider sx={{ mx: 2, my: 1, opacity: 0.35, borderColor: `${primary}22` }} />}
            <SectionLabel title={section.title} />
            <List disablePadding sx={{ mt: 0.5 }}>
              {section.items.map(item => (
                <NavItem
                  key={item.to}
                  to={item.to}
                  icon={item.icon}
                  text={item.text}
                  isActive={
                    location.pathname === item.path || location.pathname.startsWith(item.path + '/')
                  }
                />
              ))}
            </List>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export const GOLFOS_MENU_WIDTH_PX = MENU_WIDTH;
